import { useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  PaginatedResult,
  PayableDueState,
  PayableInvoiceDetail,
  PayableInvoiceSummary,
  PayableSettlement,
  PayableSupplierPosition,
  PayablesAgeingReport,
  PayablesSummary,
  SupplierPaymentDetail,
  SupplierPaymentMethod,
  SupplierPaymentStatus,
  SupplierPaymentSummary,
} from '@optiflow/types';
import { api } from '@/services/apiClient';
import { useIdempotentMutation } from '@/services/useIdempotentMutation';
import { purchaseInvoiceKeys } from '@/modules/purchasing/api/purchase-invoices.api';

/** Branch and supplier narrowing shared by every payables read. */
export interface PayablesScopeParams {
  branchId?: string;
  supplierId?: string;
  dueSoonWithinDays?: number;
}

export interface PayableInvoiceListParams extends PayablesScopeParams {
  page?: number;
  pageSize?: number;
  search?: string;
  settlement?: PayableSettlement | '';
  due?: PayableDueState | '';
  outstandingOnly?: boolean;
  includeVoided?: boolean;
  dueFrom?: string;
  dueTo?: string;
}

export interface SupplierPaymentListParams {
  page?: number;
  pageSize?: number;
  search?: string;
  supplierId?: string;
  branchId?: string;
  method?: SupplierPaymentMethod | '';
  status?: SupplierPaymentStatus | '';
  dateFrom?: string;
  dateTo?: string;
}

/**
 * One invoice's share of a payment being recorded.
 *
 * The whole request body, in fact: the server reads the supplier, the branch and the currency off
 * these invoices and adds the amounts up itself, so there is no total to send and nothing the
 * client could get wrong about who is being paid.
 */
export interface SupplierPaymentAllocationInput {
  purchaseInvoiceId: string;
  amountMinor: number;
}

export interface PostSupplierPaymentBody {
  paymentDate?: string;
  method: SupplierPaymentMethod;
  externalReference?: string;
  allocations: SupplierPaymentAllocationInput[];
}

export const payablesKeys = {
  all: ['payables'] as const,
  summary: (params: PayablesScopeParams) => [...payablesKeys.all, 'summary', params] as const,
  ageing: (params: PayablesScopeParams) => [...payablesKeys.all, 'ageing', params] as const,
  suppliers: (params: PayablesScopeParams) => [...payablesKeys.all, 'suppliers', params] as const,
  invoices: (params: PayableInvoiceListParams) =>
    [...payablesKeys.all, 'invoices', params] as const,
  invoice: (id: string) => [...payablesKeys.all, 'invoice', id] as const,
  payments: (params: SupplierPaymentListParams) =>
    [...payablesKeys.all, 'payments', params] as const,
  payment: (id: string) => [...payablesKeys.all, 'payment', id] as const,
};

const scope = (params: PayablesScopeParams) => ({
  ...(params.branchId ? { branchId: params.branchId } : {}),
  ...(params.supplierId ? { supplierId: params.supplierId } : {}),
  ...(params.dueSoonWithinDays ? { dueSoonWithinDays: params.dueSoonWithinDays } : {}),
});

/**
 * What is owed right now, worked out by the server.
 *
 * Never added up in the browser from a page of invoices: a page is not the tenant, and a figure
 * that silently means "the first twenty" is worse than no figure at all.
 */
export function usePayablesSummaryQuery(
  params: PayablesScopeParams,
  { enabled = true }: { enabled?: boolean } = {},
) {
  return useQuery({
    queryKey: payablesKeys.summary(params),
    queryFn: () => api.get<PayablesSummary>('/payables/summary', { params: scope(params) }),
    placeholderData: (previous) => previous,
    enabled,
  });
}

export function usePayablesAgeingQuery(
  params: PayablesScopeParams,
  { enabled = true }: { enabled?: boolean } = {},
) {
  return useQuery({
    queryKey: payablesKeys.ageing(params),
    queryFn: () => api.get<PayablesAgeingReport>('/payables/ageing', { params: scope(params) }),
    placeholderData: (previous) => previous,
    enabled,
  });
}

/** What each supplier is owed, worst overdue first. */
export function usePayableSuppliersQuery(
  params: PayablesScopeParams,
  { enabled = true }: { enabled?: boolean } = {},
) {
  return useQuery({
    queryKey: payablesKeys.suppliers(params),
    queryFn: () =>
      api.get<PayableSupplierPosition[]>('/payables/suppliers', { params: scope(params) }),
    placeholderData: (previous) => previous,
    enabled,
  });
}

export function usePayableInvoicesQuery(
  params: PayableInvoiceListParams,
  { enabled = true }: { enabled?: boolean } = {},
) {
  return useQuery({
    queryKey: payablesKeys.invoices(params),
    queryFn: () =>
      api.get<PaginatedResult<PayableInvoiceSummary>>('/payables/invoices', {
        params: {
          page: params.page ?? 1,
          pageSize: params.pageSize ?? 20,
          ...scope(params),
          ...(params.search?.trim() ? { search: params.search.trim() } : {}),
          ...(params.settlement ? { settlement: params.settlement } : {}),
          ...(params.due ? { due: params.due } : {}),
          ...(params.outstandingOnly === undefined
            ? {}
            : { outstandingOnly: params.outstandingOnly }),
          ...(params.includeVoided ? { includeVoided: true } : {}),
          ...(params.dueFrom ? { dueFrom: params.dueFrom } : {}),
          ...(params.dueTo ? { dueTo: params.dueTo } : {}),
        },
      }),
    placeholderData: (previous) => previous,
    enabled,
  });
}

export function usePayableInvoiceQuery(id: string | undefined) {
  return useQuery({
    queryKey: payablesKeys.invoice(id ?? ''),
    queryFn: () => api.get<PayableInvoiceDetail>(`/payables/invoices/${id ?? ''}`),
    enabled: Boolean(id),
  });
}

export function useSupplierPaymentsQuery(
  params: SupplierPaymentListParams,
  { enabled = true }: { enabled?: boolean } = {},
) {
  return useQuery({
    queryKey: payablesKeys.payments(params),
    queryFn: () =>
      api.get<PaginatedResult<SupplierPaymentSummary>>('/payables/payments', {
        params: {
          page: params.page ?? 1,
          pageSize: params.pageSize ?? 20,
          ...(params.search?.trim() ? { search: params.search.trim() } : {}),
          ...(params.supplierId ? { supplierId: params.supplierId } : {}),
          ...(params.branchId ? { branchId: params.branchId } : {}),
          ...(params.method ? { method: params.method } : {}),
          ...(params.status ? { status: params.status } : {}),
          ...(params.dateFrom ? { dateFrom: params.dateFrom } : {}),
          ...(params.dateTo ? { dateTo: params.dateTo } : {}),
        },
      }),
    placeholderData: (previous) => previous,
    enabled,
  });
}

export function useSupplierPaymentQuery(id: string | undefined) {
  return useQuery({
    queryKey: payablesKeys.payment(id ?? ''),
    queryFn: () => api.get<SupplierPaymentDetail>(`/payables/payments/${id ?? ''}`),
    enabled: Boolean(id),
  });
}

/** Everything payables reads, plus the purchasing invoice screens, which now show a different balance. */
function invalidateSettlement(queryClient: ReturnType<typeof useQueryClient>) {
  void queryClient.invalidateQueries({ queryKey: payablesKeys.all });
  void queryClient.invalidateQueries({ queryKey: purchaseInvoiceKeys.all });
}

/**
 * Records money paid against posted supplier invoices.
 *
 * One key per posting intent: a double click, or a retry after a lost response, pays the supplier
 * once. Everything derived from the allocations is refetched afterwards rather than adjusted here —
 * what an invoice owes is the server's answer, and a locally subtracted figure left standing as
 * truth is how two screens start disagreeing.
 */
export function usePostSupplierPaymentMutation() {
  const queryClient = useQueryClient();

  return useIdempotentMutation<SupplierPaymentDetail, PostSupplierPaymentBody>(
    (payload, config) => api.post<SupplierPaymentDetail>('/payables/payments', payload, config),
    { onSuccess: () => invalidateSettlement(queryClient) },
  );
}

/**
 * Voids one, with a reason.
 *
 * OptiFlow stops counting it as settlement; it makes no claim that a bank or card transaction was
 * reversed. The document, its reference and every allocation stay exactly where they are.
 */
export function useVoidSupplierPaymentMutation() {
  const queryClient = useQueryClient();

  return useIdempotentMutation<SupplierPaymentDetail, { id: string; reason: string }>(
    ({ id, reason }, config) =>
      api.post<SupplierPaymentDetail>(`/payables/payments/${id}/void`, { reason }, config),
    { onSuccess: () => invalidateSettlement(queryClient) },
  );
}
